import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useQuery, useMutation, useToast } from '@webapps/reactjs-tech-components';
import AvatarCompany from 'components/Avatar/AvatarCompany';

const styles = {
  root: {
    display: 'none',
    padding: '24px',
    marginBottom: '16px',
    width: '100%',
    height: "calc(1100px)",
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  header: {
    marginBottom: '16px',
  },
  schoolInfo: {
    marginBottom: '32px',
  },
  table: {
    marginTop: '16px',
    width: '100%',
    // backgroundColor: "red"
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  tableCell: {
    // padding: '16px',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '13px',
    paddingTop: '16px',
    paddingBottom: '16px',
    textAlign: 'start',
    marginLeft: '5px'
    // width: 'calc(20% - 32px)',
  },
  footerContainer: {
    marginTop: '40px',
    textAlign: 'right',
  },
  contentContainer: {
    marginBottom: '24px',
  },
  text13: {
    fontSize: '13px',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: '6px'
  }

};

function formatHoursAndMinutes(hoursDecimal) {

  const heures = Math.floor(hoursDecimal);
  const minutes = Math.round((hoursDecimal - heures) * 60);
  let result = '';

  if (heures > 0 && minutes > 0) {
    result = `${heures}h${minutes.toString().padStart(2, '0')}`;
  } else if (heures > 0) {
    result = `${heures}h`;
  } else if (minutes > 0) {
    result = `${minutes}mn`;
  } else {
    result = '0h';
  }

  return result;
}

const StudentStatementForMailing = forwardRef(({ student, classroom, onDownloadComplete, downloadStatement, absenceToExport, setEmailSendedLoading, businessEmail }, ref) => {

  const currentDate = format(new Date(), 'dd MMMM yyyy', { locale: fr });
  const contentRef = useRef(null);
  const [isLoadingAbsences, setIsLoadingAbsences] = useState(true);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const loadingStateRef = useRef({ absences: true, image: true });
  const isDownloadingRef = useRef(false);
  const city = classroom?.school?.address?.city;
  const startPeriodDate = absenceToExport[0]?.startDate;
  const endPeriodDate = absenceToExport.length > 1 ? absenceToExport[absenceToExport.length - 1]?.endDate : absenceToExport[0]?.endDate;
  const reason = absenceToExport[0]?.reason;
  const totalHours = absenceToExport[0]?.totalHours;

  const getAbsenceLocationLabel = (absences) => {
    const locations = new Set(absences.map(a => a.absenceLocation));

    const labels = [];

    if (locations.has("SCHOOL")) labels.push("À l'école");
    if (locations.has("COMPANY")) labels.push("En entreprise");

    return labels.join(" / ");
  };

  const addToast = useToast();

  const handleImageLoad = () => {
    setIsLoadingImage(false);
    loadingStateRef.current.image = false;
  };

  const handleImageError = () => {
    setIsLoadingImage(false);
    loadingStateRef.current.image = false;
  };

  useEffect(() => {
    loadingStateRef.current.absences = isLoadingAbsences;
    loadingStateRef.current.image = isLoadingImage;

    // Nous ne déclenchons rien automatiquement ici
    // Le téléchargement sera lancé via la ref lorsque handlePeriodConfirm est appelé
  }, [isLoadingAbsences, isLoadingImage, absenceToExport, student?.id]);


  useEffect(() => {

    if (student?.id) {
      setIsLoadingImage(true);
      loadingStateRef.current.image = true;
    }
  }, [student?.id]);

  useEffect(() => {
    if (isLoadingImage) {
      const timer = setTimeout(() => {
        setIsLoadingImage(false);
        loadingStateRef.current.image = false;
      }, 2000); // 2 seconds timeout
      return () => clearTimeout(timer);
    }
  }, [isLoadingImage]);

  const downloadPdf = async () => {
    try {
      // Prevent multiple downloads
      if (isDownloadingRef.current) {
        return;
      }

      isDownloadingRef.current = true;

      if (!contentRef.current) {
        console.error("Content ref is not available");
        isDownloadingRef.current = false;
        return;
      }

      // Fonction pour continuer le téléchargement une fois que tout est prêt
      const proceedWithDownload = async () => {
        try {
          // Récupérer le contenu HTML à partir de la référence
          const htmlContent = contentRef.current?.outerHTML;
          if (!htmlContent) {
            throw new Error("Le contenu HTML est introuvable.");
          }


          // Envoie de la requête pour générer le PDF
          const response = await fetch(`${process.env.NX_PROXY_API}/generate-statement`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ html: htmlContent }),
          });


          // Vérification de la réponse
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`La réponse du réseau n'était pas correcte : ${errorText}`);
          }


          // Récupération de la réponse en tant que Blob (fichier PDF)
          const blob = await response.blob();

          // Envoi du fichier Blob à une autre route backend
          await sendPdfToAnotherBackend(blob);

        } catch (error) {
          console.error("Erreur lors de la génération ou de l'envoi du PDF :", error);
        }
      };


      // Fonction pour envoyer le fichier Blob à une autre route backend
      const sendPdfToAnotherBackend = async (pdfBlob) => {
        try {
          const formData = new FormData();
          formData.append("file", pdfBlob, "generated-file.pdf");

          // Ajouter l'absence en tant que string (JSON)
          let absence = `{
              "recipient":{
                  "firstname": "${student?.firstname}",
                  "lastname": "${student?.lastname}",
                  "email": "${student?.email}",
                  "gender": "${student?.gender ? student?.gender.toLowerCase() : "other"}",
                  "id": "${student?.id}",
                  "schoolId": "${classroom?.school?.id}"
                  },
                  "absence": {
                    "startDate": "${startPeriodDate}",
                    "endDate": "${endPeriodDate}",
                    "reason": "${reason}"
                    },
              "business_email": "${businessEmail}",
              "business_id": "${student?.team?.business?.id ? student?.team?.business?.id : null}",
              "issuer": "SCHOOL"
          }`;

          absence = absence.trim();
          formData.append("absence", absence);

          // Envoi au backend
          const response = await fetch(`${process.env.NX_REST_API}/schools/module/absences/upload`, {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Erreur d'envoi du fichier : ${errorText}`);
          }

        } catch (error) {
          console.error("Erreur lors de l'envoi du PDF :", error);
          addToast({
            title: "Error",
            message: "Erreur l'email n'a pas pu être envoyé",
            type: "error",
          });
        } finally {
          setEmailSendedLoading(true);
          addToast({
            title: "Success",
            message: "Email envoyé avec succès",
            type: "success",
          });
        }
      };

      return proceedWithDownload();
    } catch (error) {
      console.error('Error generating PDF:', error);
      isDownloadingRef.current = false;
      if (onDownloadComplete) {
        onDownloadComplete();
      }
    }
  };

  const sendEmail = async () => {

    await downloadPdf();
    onDownloadComplete();
  }

  useEffect(() => {
    if (!isLoadingImage) {
      sendEmail();
    }
  }, [isLoadingImage])

  return (
    <Paper style={styles.root} ref={contentRef} id="hidden-content">
      <div>
        <div style={styles.header}>
          <div style={styles.avatarContainer}>
            <AvatarCompany
              height={80}
              idCompany={classroom?.school?.id}
              name={classroom?.school?.name}
              width={80}
              onLoad={handleImageLoad}
              onError={handleImageError}
              toHtml={true}
            />
          </div>
          <div style={styles.schoolInfo}>
            <Typography>{student?.firstname} {student?.lastname}</Typography>
            <Typography>{student?.address?.label}</Typography>
            <Typography>{student?.address?.cityCode} {student?.address?.city}</Typography>
          </div>
          <Typography>{city ? `${city} le ${currentDate}` : `Le ${currentDate}`}</Typography>
        </div>

        <div style={styles.contentContainer}>
          <Typography style={styles.title}>
            Absence du {startPeriodDate === endPeriodDate ? format(new Date(startPeriodDate), 'dd/MM/yyyy') : `${format(new Date(startPeriodDate), 'dd/MM/yyyy')} au ${format(new Date(endPeriodDate), 'dd/MM/yyyy')}`}
          </Typography>

          <Typography style={styles.text13}>
            Vous trouverez ci-dessous le récapitulatif des absences et des retards de l'{student?.gender === "MALE" ? "étudiant" : student?.gender === "FEMALE" ? "étudiante" : "étudiant(e)"} {student?.firstname} {student?.lastname} en {classroom?.label}
          </Typography>

          <Typography style={styles.text13}>
            Nous vous rappelons que toute absence ou tout retard doit être justifié auprès du secrétariat de l'accueil
          </Typography>
        </div>

        {
          absenceToExport && (
            <TableContainer style={styles.table} className="student-statement-table">
              <Table className="student-statement-table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Type</TableCell>
                    <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Date</TableCell>
                    <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Description</TableCell>
                    <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Temps</TableCell>
                    <TableCell style={{ ...styles.tableCell, ...styles.tableHeader, ...styles.text13, width: '20%' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell style={styles.tableCell}>Absence</TableCell>
                    <TableCell style={styles.tableCell}>
                      {startPeriodDate === endPeriodDate ? format(new Date(startPeriodDate), 'dd/MM/yyyy') : `${format(new Date(startPeriodDate), 'dd/MM/yyyy')} au ${format(new Date(endPeriodDate), 'dd/MM/yyyy')}`}
                    </TableCell>
                    <TableCell style={styles.tableCell}>{reason}</TableCell>
                    <TableCell style={styles.tableCell}>{formatHoursAndMinutes(totalHours)}</TableCell>
                    <TableCell style={styles.tableCell}>{getAbsenceLocationLabel(absenceToExport)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>)
        }
      </div>

      <div>

        <div style={styles.footerContainer}>
          <Typography>{classroom?.school?.name}</Typography>
        </div>
      </div>
    </Paper>
  );
});

export default StudentStatementForMailing;